import React, { useCallback, useEffect, useMemo, useState, useRef, Component } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { DataTable, Column } from '../shared/DataTable';
import { MasteryBar } from '../shared/MasteryBar';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Badge } from '../../../components/ui/Badge';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { CheckCircle2, XCircle, Search, Filter, BookOpen, FileText, X, Lightbulb, AlertTriangle, ShieldCheck, MessageCircle } from 'lucide-react';
import { TAXONOMY, QUESTIONS_BY_FAMILY } from '../../../content';
import { resolveUnifiedSupport, FAMILY_SUPPORT_REGISTRY, validateHintQuality, type ResolvedSupport, type HintStep, type HintQualityIssue } from '../../../support';
import { HintQualityWidget } from '../content/support/HintQualityWidget';
/**
 * Per-hintType styling for the admin preview panel.
 * Mirrors `HINT_TYPE_META` in `src/practice/components/SubSkillCheatSheet.tsx`
 * so editors see the same colour cues as the child sees in the Spiekbriefje.
 * Keep these in sync: indigo=core, amber=example, rose=pitfall, emerald=transfer.
 */
const ADMIN_HINT_TYPE_META: Record<'core' | 'example' | 'pitfall' | 'transfer' | 'default', {
  emoji: string;
  label: string;
  cardBg: string;
  cardBorder: string;
  titleText: string;
  bodyText: string;
}> = {
  core: {
    emoji: '🧠',
    label: 'Kern',
    cardBg: 'bg-indigo-50/60',
    cardBorder: 'border-indigo-200',
    titleText: 'text-indigo-800',
    bodyText: 'text-indigo-900'
  },
  example: {
    emoji: '💡',
    label: 'Voorbeeld',
    cardBg: 'bg-amber-50/60',
    cardBorder: 'border-amber-200',
    titleText: 'text-amber-800',
    bodyText: 'text-amber-900'
  },
  pitfall: {
    emoji: '⚠️',
    label: 'Let op',
    cardBg: 'bg-rose-50/60',
    cardBorder: 'border-rose-200',
    titleText: 'text-rose-800',
    bodyText: 'text-rose-900'
  },
  transfer: {
    short: '→',
    label: 'Verbinding',
    cardBg: 'bg-emerald-50/60',
    cardBorder: 'border-emerald-200',
    titleText: 'text-emerald-800',
    bodyText: 'text-emerald-900'
  },
  default: {
    short: '?',
    label: 'Hint',
    cardBg: 'bg-gray-50/80',
    cardBorder: 'border-gray-200',
    titleText: 'text-gray-700',
    bodyText: 'text-gray-800'
  }
};
interface FamilySupportRow {
  id: string;
  familyId: string;
  module: string;
  tier: number;
  questionCount: number;
  support: ResolvedSupport;
  completeness: number;
  hintCount: number;
  registryHints: HintStep[];
  /**
   * Bubble hint read straight from the registry entry. Lives outside
   * `ResolvedSupport` because the resolver doesn't expose it (it's only
   * consumed by CheatSheetDrawer, not by FeedbackSplash). Admin preview
   * shows it as "Djay zegt" so editors can review the voice line.
   */
  bubbleHint?: string;
  inRegistry: boolean;
  qualityIssueCount: number;
}
export function SupportContentPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [coverageFilter, setCoverageFilter] = useState<'all' | 'full' | 'partial' | 'none'>('all');
  const [hintFilter, setHintFilter] = useState<'all' | 'enriched' | 'sparse' | 'none' | 'issues'>('all');
  const [selectedFamily, setSelectedFamily] = useState<FamilySupportRow | null>(null);
  // Hint quality report — single source of truth. Used by widget AND by the
  // table + preview to surface per-family issues inline.
  const qualityReport = useMemo(() => validateHintQuality(), []);
  const issuesByFamily = useMemo(() => {
    const map = new Map<string, HintQualityIssue[]>();
    for (const issue of qualityReport.issues) {
      const list = map.get(issue.familyId) ?? [];
      list.push(issue);
      map.set(issue.familyId, list);
    }
    return map;
  }, [qualityReport]);
  const allData: FamilySupportRow[] = useMemo(() => {
    return TAXONOMY.map(family => {
      const support = resolveUnifiedSupport(family.familyId);
      const registryEntry = FAMILY_SUPPORT_REGISTRY[family.familyId];
      const registryHints = registryEntry?.hints || [];
      return {
        id: family.familyId,
        familyId: family.familyId,
        module: family.module,
        tier: family.tier,
        questionCount: QUESTIONS_BY_FAMILY.get(family.familyId)?.length ?? 0,
        support,
        completeness: support.coverage.completeness,
        hintCount: registryHints.length,
        registryHints,
        bubbleHint: registryEntry?.bubbleHint,
        inRegistry: !!registryEntry,
        qualityIssueCount: issuesByFamily.get(family.familyId)?.length ?? 0
      };
    });
  }, [issuesByFamily]);
  const stats = useMemo(() => {
    const total = allData.length;
    const inRegistry = allData.filter(d => d.inRegistry).length;
    const noHints = allData.filter(d => d.hintCount === 0).length;
    const totalHints = allData.reduce((sum, d) => sum + d.hintCount, 0);
    const overallCoverage = Math.round(inRegistry / total * 100);
    const cleanHints = Math.max(0, totalHints - qualityReport.issues.length);
    const hintQualityPct = totalHints > 0 ? Math.round(cleanHints / totalHints * 100) : 100;
    return {
      total,
      inRegistry,
      noHints,
      totalHints,
      overallCoverage,
      hintQualityPct
    };
  }, [allData, qualityReport]);
  const filteredData = useMemo(() => {
    return allData.filter(row => {
      if (searchQuery && !row.familyId.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (moduleFilter !== 'all' && row.module !== moduleFilter) return false;
      if (coverageFilter === 'full' && row.completeness !== 100) return false;
      if (coverageFilter === 'partial' && (row.completeness === 0 || row.completeness === 100)) return false;
      if (coverageFilter === 'none' && row.completeness !== 0) return false;
      if (hintFilter === 'enriched' && row.hintCount < 2) return false;
      if (hintFilter === 'sparse' && row.hintCount !== 1) return false;
      if (hintFilter === 'none' && row.hintCount !== 0) return false;
      if (hintFilter === 'issues' && row.qualityIssueCount === 0) return false;
      return true;
    });
  }, [allData, searchQuery, moduleFilter, coverageFilter, hintFilter]);
  const modules = useMemo(() => Array.from(new Set(TAXONOMY.map(f => f.module))).sort(), []);
  const exampleQuestion = useMemo(() => {
    if (!selectedFamily) return null;
    const questions = QUESTIONS_BY_FAMILY.get(selectedFamily.familyId);
    return questions && questions.length > 0 ? questions[0] : null;
  }, [selectedFamily]);
  /** Issues for the currently selected family — used to annotate hint cards. */
  const selectedFamilyIssues = useMemo(() => {
    if (!selectedFamily) return new Map<number, HintQualityIssue[]>();
    const map = new Map<number, HintQualityIssue[]>();
    const list = issuesByFamily.get(selectedFamily.familyId) ?? [];
    for (const issue of list) {
      const arr = map.get(issue.hintIndex) ?? [];
      arr.push(issue);
      map.set(issue.hintIndex, arr);
    }
    return map;
  }, [selectedFamily, issuesByFamily]);
  // Allow widget rows + URL hash to jump to a family.
  const previewRef = useRef<HTMLDivElement | null>(null);
  const selectFamilyById = useCallback((familyId: string) => {
    const row = allData.find(r => r.familyId === familyId) ?? null;
    setSelectedFamily(row);
    // Smooth-scroll the preview into view on mobile.
    requestAnimationFrame(() => {
      previewRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  }, [allData]);
  useEffect(() => {
    if (selectedFamily) return;
    const hash = window.location.hash.replace(/^#family=/, '');
    if (hash) selectFamilyById(decodeURIComponent(hash));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const renderCheck = (hasItem: boolean) => {
    return hasItem ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-6652" /> : <XCircle className="w-4 h-4 text-gray-300" data-id="element-6653" />;
  };
  const renderHintCount = (count: number, issueCount: number) => {
    if (count === 0) {
      return <span className="inline-flex items-center gap-1 text-xs text-gray-400" data-id="element-6654">
          <XCircle className="w-3.5 h-3.5" data-id="element-6655" />
          <span data-id="element-6656">0</span>
        </span>;
    }
    return <span className="inline-flex items-center gap-1.5" data-id="element-6657">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium border ${count === 1 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`} data-id="element-6658">
          {count === 1 ? <AlertTriangle className="w-3 h-3" data-id="element-6659" /> : <Lightbulb className="w-3 h-3" data-id="element-6660" />}
          <span data-id="element-6661">{count}</span>
        </span>
        {issueCount > 0 && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 text-[10px] font-bold border border-rose-200" title={`${issueCount} hint-issue${issueCount === 1 ? '' : 's'}`} data-id="element-6662">
            <AlertTriangle className="w-2.5 h-2.5" data-id="element-6663" />
            {issueCount}
          </span>}
      </span>;
  };
  const columns: Column<FamilySupportRow>[] = [{
    key: 'familyId',
    label: 'Family ID',
    render: val => <span className="font-mono text-xs font-medium text-gray-700" data-id="element-6664">
          {val}
        </span>
  }, {
    key: 'module',
    label: 'Domein',
    render: val => <Badge variant="secondary" data-id="element-6665">{val}</Badge>
  }, {
    key: 'tier',
    label: 'Tier',
    render: val => <Badge variant="secondary" data-id="element-6666">T{val}</Badge>
  }, {
    key: 'questionCount',
    label: 'Vragen',
    render: val => <span className="text-xs font-medium text-gray-600" data-id="element-6667">{val}</span>
  }, {
    key: 'explanation',
    label: 'Uitleg',
    render: (_, row) => renderCheck(row.support.coverage.hasExplanation)
  }, {
    key: 'hintCount',
    label: 'Hints',
    render: (_, row) => renderHintCount(row.hintCount, row.qualityIssueCount)
  }, {
    key: 'completeness',
    label: 'Compleetheid',
    render: val => <MasteryBar value={val} data-id="element-6668" />
  }];
  return <PageLayout title="Ondersteuning Content" subtitle="Uitleg, hints en spiekbriefjes per family — compleet overzicht van alle ondersteuningscontent." actions={<ConnectionBadge status="live" data-id="element-6670" />} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Ondersteuning'
  }]} data-id="element-6669">
      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6" data-id="element-6671">
        <StatCard label="Dekking" value={`${stats.overallCoverage}%`} icon={<BookOpen className="w-5 h-5" data-id="element-6673" />} color={stats.overallCoverage === 100 ? 'emerald' : stats.overallCoverage >= 80 ? 'amber' : 'blue'} data-id="element-6672" />
        <StatCard label="In Register" value={stats.inRegistry} icon={<CheckCircle2 className="w-5 h-5" data-id="element-6675" />} color="emerald" data-id="element-6674" />
        <StatCard label="Totaal Hints" value={stats.totalHints} icon={<Lightbulb className="w-5 h-5" data-id="element-6677" />} color="blue" data-id="element-6676" />
        <StatCard label="Zonder Hints" value={stats.noHints} icon={<XCircle className="w-5 h-5" data-id="element-6679" />} color={stats.noHints > 0 ? 'red' : 'emerald'} data-id="element-6678" />
        <StatCard label="Hint-kwaliteit" value={`${stats.hintQualityPct}%`} icon={<ShieldCheck className="w-5 h-5" data-id="element-6681" />} color={stats.hintQualityPct === 100 ? 'emerald' : stats.hintQualityPct >= 80 ? 'amber' : 'red'} data-id="element-6680" />
      </div>

      {/* Hint quality widget — collapsible. F-H6: pass the shared report so
                  validateHintQuality() runs once per page render, not twice. */}
      <HintQualityWidget report={qualityReport} onSelectFamily={selectFamilyById} data-id="element-6682" />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6 bg-white p-4 rounded-xl border border-gray-200 shadow-sm items-center" data-id="element-6683">
        <div className="flex-1 w-full" data-id="element-6684">
          <Input type="text" placeholder="Zoek op Family ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-6686" />} variant="admin" size="sm" data-id="element-6685" />
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto" data-id="element-6687">
          <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500 font-medium px-2" data-id="element-6688">
            <Filter className="w-4 h-4" data-id="element-6689" />
            <span data-id="element-6690">Filters</span>
          </div>
          <Select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} options={[{
          value: 'all',
          label: 'Alle domeinen'
        }, ...modules.map(m => ({
          value: m,
          label: m
        }))]} variant="admin" size="sm" fullWidth={false} data-id="element-6691" />
          <Select value={coverageFilter} onChange={e => setCoverageFilter(e.target.value as typeof coverageFilter)} options={[{
          value: 'all',
          label: 'Alle dekkingen'
        }, {
          value: 'full',
          label: 'Volledig (100%)'
        }, {
          value: 'partial',
          label: 'Gedeeltelijk (1-99%)'
        }, {
          value: 'none',
          label: 'Geen (0%)'
        }]} variant="admin" size="sm" fullWidth={false} data-id="element-6692" />
          <Select value={hintFilter} onChange={e => setHintFilter(e.target.value as typeof hintFilter)} options={[{
          value: 'all',
          label: 'Alle hints'
        }, {
          value: 'enriched',
          label: 'Verrijkt (≥2)'
        }, {
          value: 'sparse',
          label: 'Schaars (1)'
        }, {
          value: 'none',
          label: 'Geen hints'
        }, {
          value: 'issues',
          label: 'Met kwaliteits-issues'
        }]} variant="admin" size="sm" fullWidth={false} data-id="element-6693" />
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-gray-500 font-medium" data-id="element-6694">
        {filteredData.length} van {allData.length} families
      </div>

      <div className="flex flex-col lg:flex-row gap-6" data-id="element-6695">
        {/* Table */}
        <div className={`flex-1 transition-all ${selectedFamily ? 'lg:w-2/3' : 'w-full'}`} data-id="element-6696">
          <DataTable columns={columns} data={filteredData} onRowClick={setSelectedFamily} emptyMessage="Geen families gevonden met deze filters." data-id="element-6697" />
        </div>

        {/* Preview Panel */}
        {selectedFamily && <div ref={previewRef} className="w-full lg:w-1/3 flex-shrink-0" data-id="element-6698">
            <Card className="sticky top-6 flex flex-col max-h-[calc(100vh-120px)] shadow-sm border-gray-200" data-id="element-6699">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/80 rounded-t-xl" data-id="element-6700">
                <div data-id="element-6701">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-6702">
                    <FileText className="w-4 h-4 text-blue-500" data-id="element-6703" />
                    Content Preview
                  </h3>
                  <p className="text-xs font-mono text-gray-500 mt-1" data-id="element-6704">
                    {selectedFamily.familyId}
                  </p>
                </div>
                <button onClick={() => setSelectedFamily(null)} className="p-1.5 h-auto text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors" aria-label="Sluiten" data-id="element-6705">
                  <X className="w-4 h-4" data-id="element-6706" />
                </button>
              </div>

              <div className="p-5 overflow-y-auto flex-1 space-y-6" data-id="element-6707">
                {/* Registry status */}
                <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${selectedFamily.inRegistry ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-id="element-6708">
                  {selectedFamily.inRegistry ? <>
                      <CheckCircle2 className="w-4 h-4" data-id="element-6709" /> In
                      FAMILY_SUPPORT_REGISTRY
                    </> : <>
                      <XCircle className="w-4 h-4" data-id="element-6710" /> Niet in register — geen
                      support content
                    </>}
                </div>

                {/* Family Context */}
                <div className="space-y-3" data-id="element-6711">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-6712">
                    Context
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-6713">
                    <Badge variant="secondary" data-id="element-6714">
                      Tier {selectedFamily.tier}
                    </Badge>
                    <Badge variant="secondary" data-id="element-6715">
                      {selectedFamily.questionCount} Vragen
                    </Badge>
                    <Badge variant="secondary" data-id="element-6716">{selectedFamily.module}</Badge>
                    {selectedFamily.qualityIssueCount > 0 && <Badge variant="danger" icon={<AlertTriangle className="w-3 h-3" data-id="element-6718" />} data-id="element-6717">
                        {selectedFamily.qualityIssueCount} hint-issue
                        {selectedFamily.qualityIssueCount === 1 ? '' : 's'}
                      </Badge>}
                  </div>
                </div>

                {/* Example Question */}
                {exampleQuestion && <div className="space-y-3 pt-4 border-t border-gray-100" data-id="element-6719">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-6720">
                      Voorbeeld Vraag
                    </h4>
                    <div className="bg-gray-50 p-3.5 rounded-lg border border-gray-200" data-id="element-6721">
                      <p className="text-sm text-gray-800 leading-relaxed mb-3" data-id="element-6722">
                        {exampleQuestion.question}
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-6723">
                        <Badge variant="secondary" data-id="element-6724">
                          {exampleQuestion.questionType}
                        </Badge>
                        <Badge variant="secondary" data-id="element-6725">
                          {exampleQuestion.levelCode}
                        </Badge>
                      </div>
                    </div>
                  </div>}

                {/* Bubble hint — what Djay says in the floating speech bubble.
                 Added 17 May 2026 as part of the Spiekbriefje voice upgrade.
                 Shown above the explanation because that's how the child
                 perceives the spiekbriefje: bubble first, paper sheet below. */}
                <div className="space-y-3 pt-4 border-t border-gray-100" data-id="element-6726">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2" data-id="element-6727">
                    {selectedFamily.bubbleHint ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-6728" /> : <XCircle className="w-4 h-4 text-gray-300" data-id="element-6729" />}{' '}
                    Djay zegt (bubble)
                  </h4>
                  {selectedFamily.bubbleHint ? <div className="bg-cyan-50/60 p-4 rounded-lg border border-cyan-200 relative" data-id="element-6730">
                      <div className="flex items-start gap-2.5" data-id="element-6731">
                        <MessageCircle className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" data-id="element-6732" />
                        <p className="text-sm text-cyan-900 italic leading-relaxed" data-id="element-6733">
                          "{selectedFamily.bubbleHint}"
                        </p>
                      </div>
                      <p className="text-[11px] text-cyan-700/70 mt-2 pl-6" data-id="element-6734">
                        {selectedFamily.bubbleHint.length} tekens · max ~120
                      </p>
                    </div> : <p className="text-sm text-gray-400 italic bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-6735">
                      Geen bubbleHint — drawer leidt automatisch een opener af
                      uit de uitleg. Voeg er één toe voor een lekkere
                      coach-toon.
                    </p>}
                </div>

                {/* Explanation */}
                <div className="space-y-3 pt-4 border-t border-gray-100" data-id="element-6736">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2" data-id="element-6737">
                    {selectedFamily.support.explanation?.body ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-6738" /> : <XCircle className="w-4 h-4 text-gray-300" data-id="element-6739" />}{' '}
                    Meester Djay legt uit
                  </h4>
                  {selectedFamily.support.explanation?.body ? <div className="bg-orange-50/50 p-4 rounded-lg border border-orange-100" data-id="element-6740">
                      {(() => {
                  const body = selectedFamily.support.explanation.body;
                  const lines = body.split('\n').filter((l: string) => l.trim().length > 0);
                  if (lines.length <= 1) {
                    return <p className="text-sm text-orange-800 whitespace-pre-wrap" data-id="element-6741">
                              {body}
                            </p>;
                  }
                  return <ol className="list-decimal list-inside space-y-1.5" data-id="element-6742">
                            {lines.map((line: string, i: number) => <li key={i} className="text-sm text-orange-800" data-id="element-6743">
                                {line.trim()}
                              </li>)}
                          </ol>;
                })()}
                      <p className="text-xs text-orange-400/80 mt-3 italic" data-id="element-6744">
                        {(() => {
                    const lines = selectedFamily.support.explanation.body.split('\n').filter((l: string) => l.trim().length > 0);
                    return lines.length > 1 ? `${lines.length} stappen (afgeleid uit howItWorks newlines)` : 'Enkele tekst (geen stappen)';
                  })()}
                      </p>
                    </div> : <p className="text-sm text-gray-400 italic bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-6745">
                      Geen uitleg beschikbaar — howItWorks ontbreekt.
                    </p>}
                  {/* S-22 (Support RC audit, Ronde 3): removed Legacy steps[]
                   preview block. `support.steps` is now hardcoded to `[]`
                   after the dead seed-override path was deleted. */}
                </div>

                {/* Hints */}
                <div className="space-y-3 pt-4 border-t border-gray-100" data-id="element-6746">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2" data-id="element-6747">
                    {renderHintCount(selectedFamily.hintCount, selectedFamily.qualityIssueCount)}{' '}
                    Hints ({selectedFamily.hintCount})
                  </h4>
                  {selectedFamily.registryHints.length > 0 ? <div className="space-y-3" data-id="element-6748">
                      {selectedFamily.registryHints.map((hint, i) => {
                  const issues = selectedFamilyIssues.get(i) ?? [];
                  const typeKey = (hint.hintType ?? 'default') as keyof typeof ADMIN_HINT_TYPE_META;
                  const meta = ADMIN_HINT_TYPE_META[typeKey] ?? ADMIN_HINT_TYPE_META.default;
                  const hasIssues = issues.length > 0;
                  return <div key={i} className={`p-3.5 rounded-lg border ${hasIssues ? 'bg-rose-50/40 border-rose-300 ring-1 ring-rose-200' : `${meta.cardBg} ${meta.cardBorder}`}`} data-id="element-6749">
                            <div className="flex items-center gap-2 mb-2" data-id="element-6750">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/70 border ${meta.cardBorder} ${meta.titleText}`} title={hint.hintType ? `hintType: ${hint.hintType}` : 'Geen hintType — valt standaard onder "core"'} data-id="element-6751">
                                <span className="font-mono" data-id="element-6752">{meta.short}</span>
                                <span data-id="element-6753">{meta.label}</span>
                                {!hint.hintType && <span className="opacity-60" data-id="element-6754">(default)</span>}
                              </span>
                            </div>
                            {hint.title && <p className={`text-xs font-bold uppercase tracking-wider mb-1.5 ${meta.titleText}`} data-id="element-6755">
                                {hint.title}
                              </p>}
                            <p className={`text-sm whitespace-pre-wrap leading-relaxed ${meta.bodyText}`} data-id="element-6756">
                              {hint.content}
                            </p>
                            {hint.visual && <div className={`mt-2 pt-2 border-t ${meta.cardBorder} flex items-center gap-1.5`} data-id="element-6757">
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.titleText.replace('text-', 'bg-')}`} data-id="element-6758" />
                                <p className={`text-xs font-medium ${meta.titleText}`} data-id="element-6759">
                                  Visueel: {hint.visual.type}
                                </p>
                              </div>}
                            {hasIssues && <div className="mt-2.5 pt-2.5 border-t border-rose-200/60 space-y-1" data-id="element-6760">
                                {issues.map((iss, idx) => <div key={idx} className="flex items-start gap-1.5 text-xs text-rose-700" data-id="element-6761">
                                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" data-id="element-6762" />
                                    <span data-id="element-6763">
                                      <span className="font-semibold uppercase tracking-wider mr-1" data-id="element-6764">
                                        {iss.kind}:
                                      </span>
                                      {iss.message}
                                    </span>
                                  </div>)}
                              </div>}
                          </div>;
                })}
                    </div> : <p className="text-sm text-gray-400 italic bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-6765">
                      Geen hints beschikbaar.
                    </p>}
                </div>

                {/* S-22 (Support RC audit, Ronde 3): removed Sessie
                 Samenvatting block — `support.afterSummary` is always
                 `null` because no registry entry populates it and the
                 seed-override path was removed. Re-add once a real
                 afterSummary source ships (e.g. per-tier resolver). */}
              </div>
            </Card>
          </div>}
      </div>
    </PageLayout>;
}